import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, money, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { CreditCard, Banknote, Wallet } from "lucide-react";

const F = ({ id, label, ...rest }) => (
  <div>
    <Label htmlFor={id} className="overline text-luxa-espresso/50">{label}</Label>
    <Input id={id} data-testid={`checkout-${id}`} className="mt-2 rounded-none bg-luxa-shell border-luxa-sand h-12" {...rest} />
  </div>
);

export default function Checkout() {
  const { items, subtotal, shipping, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("stripe");
  const [paypalOn, setPaypalOn] = useState(false);
  useEffect(() => { api.get("/payments/config").then((r) => setPaypalOn(r.data.paypal_enabled)).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    email: user?.email || "", full_name: user?.name || "", phone: "", line1: "", line2: "",
    city: "", state: "", postal_code: "", country: "US", notes: "",
  });
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your bag is empty.");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        email: f.email,
        address: { full_name: f.full_name, phone: f.phone, line1: f.line1, line2: f.line2,
          city: f.city, state: f.state, postal_code: f.postal_code, country: f.country },
        payment_method: method, notes: f.notes, origin_url: window.location.origin,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      clear();
      navigate(`/order-confirmed/${data.order.id}`);
    } catch (err) {
      toast.error(errMsg(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 lg:py-20" data-testid="checkout-page">
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter">Checkout</h1>
      <div className="mt-12 grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-20">
        <form onSubmit={submit} className="space-y-10" data-testid="checkout-form">
          <div>
            <p className="overline text-luxa-brown">01 — Contact</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-5">
              <F id="email" label="Email" type="email" required value={f.email} onChange={on("email")} />
              <F id="phone" label="Phone" required value={f.phone} onChange={on("phone")} />
            </div>
          </div>
          <div>
            <p className="overline text-luxa-brown">02 — Shipping address</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-5">
              <F id="full_name" label="Full name" required value={f.full_name} onChange={on("full_name")} />
              <F id="line1" label="Address line 1" required value={f.line1} onChange={on("line1")} />
              <F id="line2" label="Address line 2" value={f.line2} onChange={on("line2")} />
              <F id="city" label="City" required value={f.city} onChange={on("city")} />
              <F id="state" label="State / Region" required value={f.state} onChange={on("state")} />
              <F id="postal_code" label="Postal code" required value={f.postal_code} onChange={on("postal_code")} />
              <F id="country" label="Country" required value={f.country} onChange={on("country")} />
            </div>
            <Textarea data-testid="checkout-notes" rows={3} placeholder="Delivery notes (optional)" value={f.notes}
              onChange={on("notes")} className="mt-5 rounded-none bg-luxa-shell border-luxa-sand" />
          </div>
          <div>
            <p className="overline text-luxa-brown">03 — Payment</p>
            <RadioGroup value={method} onValueChange={setMethod} className="mt-5 space-y-3">
              {[["stripe", CreditCard, "Card via Stripe", "Secure hosted checkout. Test card 4242 4242 4242 4242.", true],
                ["paypal", Wallet, "PayPal", paypalOn ? "Pay with your PayPal balance or a linked card." : "Awaiting PayPal API credentials — add them to enable this option.", paypalOn],
                ["cod", Banknote, "Cash on Delivery", "Pay the courier when your parcel arrives.", true]].map(([v, Icon, t, d, enabled]) => (
                <label key={v} htmlFor={`pay-${v}`} data-testid={`payment-${v}`} onClick={() => enabled && setMethod(v)}
                  className={`flex gap-4 items-start border p-5 transition-colors duration-300 ${!enabled ? "opacity-45 cursor-not-allowed border-luxa-sand" : method === v ? "border-luxa-brown bg-luxa-shell cursor-pointer" : "border-luxa-sand hover:border-luxa-espresso/35 cursor-pointer"}`}>
                  <RadioGroupItem value={v} id={`pay-${v}`} disabled={!enabled} className="mt-1 pointer-events-none" />
                  <div>
                    <p className="font-serif text-lg flex items-center gap-2"><Icon size={16} strokeWidth={1.5} /> {t}</p>
                    <p className="mt-1 text-sm text-luxa-espresso/60">{d}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
          <Button data-testid="place-order-button" type="submit" disabled={busy || items.length === 0}
            className="w-full rounded-none bg-luxa-brown hover:bg-luxa-brown/90 text-white h-14 overline">
            {busy ? "Processing…" : method === "cod" ? `Place Order · ${money(total)}` : `Pay ${money(total)}`}
          </Button>
        </form>

        <aside className="bg-luxa-shell border border-luxa-sand p-7 h-fit lg:sticky lg:top-28" data-testid="order-summary">
          <p className="overline text-luxa-espresso/50">Order summary</p>
          <div className="mt-6 space-y-5">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-4">
                <img src={i.image} alt={i.name} className="w-16 h-20 object-cover" />
                <div className="flex-1">
                  <p className="font-serif text-lg leading-tight">{i.name}</p>
                  <p className="text-xs text-luxa-espresso/55 mt-1">Qty {i.quantity}</p>
                </div>
                <p className="text-sm">{money(i.price * i.quantity)}</p>
              </div>
            ))}
            {items.length === 0 && <p className="text-sm text-luxa-espresso/50">Your bag is empty.</p>}
          </div>
          <div className="mt-7 border-t border-luxa-sand pt-5 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{money(subtotal)}</span></div>
            <div className="flex justify-between text-luxa-espresso/60"><span>Shipping</span><span>{shipping === 0 ? "Complimentary" : money(shipping)}</span></div>
            <div className="flex justify-between font-serif text-2xl pt-3"><span>Total</span><span data-testid="checkout-total">{money(total)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
